'use client'

import { m } from 'framer-motion'
import { Sparkles } from 'lucide-react'

import type { HeroStat } from '@/lib/cases/heroStats'

interface CaseHeroProps {
  title: string
  client: string
  stats: HeroStat[]
  tags: string | null
  image?: string
}

export default function CaseHero({ title, client, stats, tags, image: _image }: CaseHeroProps) {
  const tagList = (tags || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

  return (
    <section className="relative flex min-h-[100svh] items-start justify-center overflow-hidden px-4 pt-[calc(var(--nav-offset)+1.6rem)] pb-10 sm:items-center sm:pt-12 sm:pb-8 [@media(max-height:820px)]:md:pt-8 [@media(max-height:980px)]:md:pt-10">
      <m.div className="home-container relative z-10 text-center">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 inline-flex items-center gap-3 rounded-full border border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-pink-500/10 px-5 py-2.5 sm:mb-8 [@media(max-height:820px)]:md:mb-5"
        >
          <Sparkles className="h-4 w-4 text-purple-400" />
          <span className="home-caption text-purple-200">Digital-агентство полного цикла</span>
        </m.div>

        <m.p
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.05 }}
          className="home-kicker mb-3 text-gray-300"
        >
          {client}
        </m.p>

        <m.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="home-hero-title mx-auto mb-6 w-full max-w-[min(94vw,64rem)] leading-[1.04] [text-wrap:pretty] break-words md:text-[clamp(3rem,10vw,7rem)] md:leading-[1.2] [@media(max-height:820px)]:md:mb-4 [@media(max-height:980px)]:md:mb-5"
        >
          <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
            {title}
          </span>
        </m.h1>

        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto mb-8 max-w-4xl sm:mb-12 [@media(max-height:820px)]:md:mb-7"
        >
          {tagList.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-2">
              {tagList.map((tag, i) => (
                <span
                  key={`${tag}-${i}`}
                  className="home-meta rounded-full border border-white/10 bg-white/5 px-3 py-1 text-gray-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="home-subnote text-gray-400">Кейс: проектирование, дизайн и разработка.</p>
          )}
        </m.div>

        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="hero-stats-3 flex flex-wrap justify-center gap-8 sm:gap-12 md:gap-16 [@media(max-height:820px)]:md:gap-10"
        >
          {stats.map((stat, i) => (
            <m.div
              key={`${stat.label}-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className="text-center"
            >
              <p className="hero-stat-value mb-2 bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">
                {stat.value}
              </p>
              <p className="hero-stat-label text-gray-500">{stat.label}</p>
            </m.div>
          ))}
        </m.div>
      </m.div>

      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-10 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 [@media(max-height:820px)]:hidden"
      >
        <span className="home-meta text-gray-500">Листать</span>
        <m.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="h-10 w-[1px] bg-gradient-to-b from-white/50 to-transparent"
        />
      </m.div>
    </section>
  )
}
